import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'
import { formatMoneyCents } from '@/lib/money'
import type { Association } from '@/pages/StaffSignInPage'

interface WaitlistStatusResponse {
  waitlist_entry: {
    event_id: number
    event_name: string
    ticket_type: string
    party_size: number
    status: 'waiting' | 'left' | 'offer_sent' | 'claimed' | 'lapsed' | 'closed'
    offer_deadline: string | null
    unit_price_cents: number
  }
}

type ClaimMutation = ReturnType<typeof useMutation<unknown, Error, void, unknown>>
type LeaveMutation = ReturnType<typeof useMutation<WaitlistStatusResponse, Error, void, unknown>>

export function WaitlistStatusPage({
  association,
  token,
}: {
  association: Association
  token: string
}) {
  const queryClient = useQueryClient()
  const queryKey = ['waitlist-status', token]
  const entryQuery = useQuery({
    queryKey,
    queryFn: () => getJSON<WaitlistStatusResponse>(`/api/v1/waitlist_status_links/${token}`),
    retry: false,
  })
  const leave = useMutation({
    mutationFn: () =>
      patchJSON<WaitlistStatusResponse>(`/api/v1/waitlist_status_links/${token}`, {}),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
  })
  const claim = useMutation({
    mutationFn: () => postJSON(`/api/v1/waitlist_status_links/${token}/claim`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })
  const entry = entryQuery.data?.waitlist_entry

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center px-6 text-body-sm font-medium">
          {association.name}
        </div>
      </header>
      <div className="mx-auto w-full max-w-2xl px-6 py-8">
        {entryQuery.isLoading ? (
          <p className="text-body-sm text-muted-foreground" role="status">
            Loading Waitlist status…
          </p>
        ) : entryQuery.isError || !entry ? (
          <Alert>Waitlist Entry unavailable.</Alert>
        ) : (
          <WaitlistEntryCard
            entry={entry}
            currency={association.currency ?? 'USD'}
            claim={claim}
            leave={leave}
          />
        )}
      </div>
    </main>
  )
}

function WaitlistEntryCard({
  entry,
  currency,
  claim,
  leave,
}: {
  entry: WaitlistStatusResponse['waitlist_entry']
  currency: string
  claim: ClaimMutation
  leave: LeaveMutation
}) {
  return (
    <div className="space-y-4">
      {entry.status === 'left' && (
        <Alert variant="success" className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          You left the Waitlist.
        </Alert>
      )}
      <Card>
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-body-sm">
              {entry.status === 'offer_sent' ? 'Your Waitlist Offer' : 'Your Waitlist status'}
            </CardTitle>
            <Badge variant={statusVariant(entry.status)}>{statusLabel(entry.status)}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 p-4 pt-2 text-body-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt className="text-muted-foreground">Ticket type</dt>
            <dd>{entry.ticket_type}</dd>
            <dt className="text-muted-foreground">Party size</dt>
            <dd>{entry.party_size}</dd>
            {entry.status === 'offer_sent' && entry.offer_deadline && (
              <>
                <dt className="text-muted-foreground">Claim by</dt>
                <dd>{formatDeadline(entry.offer_deadline)}</dd>
              </>
            )}
          </dl>
          {entry.status === 'offer_sent' && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-caption text-muted-foreground">
                {entry.party_size} {entry.party_size === 1 ? 'seat is' : 'seats are'} held for you ·{' '}
                {formatMoneyCents(entry.unit_price_cents, currency)} each.
              </p>
              <Button onClick={() => claim.mutate()} disabled={claim.isPending}>
                {claim.isPending ? 'Claiming…' : `Claim ${entry.party_size} seats`}
              </Button>
              {claim.isError && (
                <Alert>That offer could not be claimed. It may have expired.</Alert>
              )}
            </div>
          )}
          {entry.status === 'waiting' && (
            <LeaveWaitlistDialog pending={leave.isPending} onConfirm={() => leave.mutate()} />
          )}
          {leave.isError && <Alert>Could not leave the Waitlist. Try again.</Alert>}
        </CardContent>
      </Card>
    </div>
  )
}

function statusLabel(status: WaitlistStatusResponse['waitlist_entry']['status']) {
  return {
    waiting: 'Waiting',
    left: 'Left',
    offer_sent: 'Offer',
    claimed: 'Claimed',
    lapsed: 'Offer expired',
    closed: 'Closed',
  }[status]
}

function statusVariant(status: WaitlistStatusResponse['waitlist_entry']['status']) {
  return status === 'waiting' || status === 'lapsed' || status === 'closed' ? 'neutral' : 'success'
}

function formatDeadline(value: string) {
  return displayDateTime(value)
}

function LeaveWaitlistDialog({ pending, onConfirm }: { pending: boolean; onConfirm: () => void }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">Leave the waitlist</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Leave the Waitlist?</DialogTitle>
          <DialogDescription>
            Your entry will be marked Left. This private link will continue to show its status.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Stay on the Waitlist
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
              {pending ? 'Leaving…' : 'Leave Waitlist'}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
